import { PkBox, PkButton, PkSpinner } from '@uefadigital/panenka-react';
import useViewportSpy from 'beautiful-react-hooks/useViewportSpy';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useMemo, useRef, memo } from 'react';

import Draw from '../../../draw/Draw';
import { DrawOptionsProvider, DrawProvider } from '../../../draw/store';
import type { DrawOptions } from '../../../draw/store/types';
import { useFilters } from '../../hooks/filters';
import { useLivescoresMatches } from '../../hooks/matches';
import type { LivescoreTab } from '../../store/types';

import { trackCardElement } from '@services/GoogleTagManagerService';
import { getDrawUrl } from '@services/LinkRuleService';
import { hydrateWithTranslations } from '@services/TranslatorService';
import Image from '@shared/Image';
import LiveIndicator from '@shared/LiveIndicator';
import { useTranslateFn } from '@shared/Translate';
import { MatchStatus } from '@shared-types/match.types';
import { dateToString } from '@utilities/dateTime';
import { imgBaseUrl } from '@utilities/WindowContext';

import * as classes from './styles.module.scss';

const fallbackImg = `${imgBaseUrl}/imgml/uefacom/fallback.jpg`;
const intersectionOptions: IntersectionObserverInit = { rootMargin: '100% 0px' };

const DrawTab = ({ tab }: { tab: LivescoreTab }) => {
  const t = useTranslateFn();

  const spyRef = useRef<HTMLDivElement>(null);
  const isVisible = useViewportSpy(spyRef, intersectionOptions);
  const wasVisible = useRef(isVisible);
  wasVisible.current ||= isVisible;
  const { matches } = useLivescoresMatches(wasVisible.current ? tab : null);

  const { filters } = useFilters();
  const competition = filters.competition;

  const drawMatchesPresent = matches.length;
  const drawStatus = useMemo(() => tab.draw.status.eventStatus, [tab.draw.status.eventStatus]);

  const drawCompleted = useMemo(() => drawStatus === MatchStatus.FINISHED, [drawStatus]);
  const drawLive = useMemo(() => drawStatus === MatchStatus.LIVE, [drawStatus]);

  const showDrawResults = drawCompleted && !drawMatchesPresent;

  const subTitle = useMemo(() => {
    if (drawCompleted) return t('draw_completed');

    const city = hydrateWithTranslations(tab.draw.city)?.name;
    const date = tab.draw.date && `${dayjs(tab.draw.date).format('HH:mm')} - ${t('your_local_time')}`;
    return [city, date].filter(Boolean).join(' - ');
  }, [drawCompleted, tab.draw.date, tab.draw.city, t]);

  const drawOptions = useMemo<DrawOptions>(
    () => ({ drawId: tab.draw.id, enableTeamLink: true, calendarContext: true, noMargin: true }),
    [tab.draw.id],
  );

  const trackingData = useMemo(() => {
    let variant = 'upcoming';
    if (drawLive) variant = 'live';
    if (drawCompleted) {
      variant = showDrawResults ? 'results' : 'pots';
    }
    return {
      id: tab.draw.id,
      name: tab.draw.translations?.title?.EN,
      category: 'draw card',
      variant,
    };
  }, [drawLive, drawCompleted, tab.draw.id, tab.draw.translations?.title?.EN, showDrawResults]);

  return (
    <div ref={spyRef} className={clsx(classes.group)}>
      <div className={classes.date}>{dateToString(tab.date, 'dddd D MMMM YYYY')}</div>
      <PkBox className={clsx(classes.drawBox, showDrawResults && classes.drawBoxResults)}>
        <div
          className='pk-d--flex pk-flex--column pk-align-items--start pk-w--100'
          ref={trackCardElement(trackingData)}
        >
          {drawLive && <LiveIndicator className='pk-flex--shrink-0 pk-mb--s' size='M' label='draw_live' />}
          <div className='pk-text--text-01 pk-font-size--xl pk-font--base-bold pk-line-height--s pk-mb--xs2'>
            {hydrateWithTranslations(tab.draw).title}
          </div>
          <div className='pk-text--text-02 pk-font-size--s pk-font--base-regular pk-line-height--s'>{subTitle}</div>
          {showDrawResults ? (
            <div className='pk-w--100'>
              <div className='pk-text--text-01 pk-font-size--l pk-font--base-semibold pk-line-height--m pk-my--m'>
                {t('draw_results')}
              </div>
              <DrawOptionsProvider options={drawOptions}>
                <DrawProvider>
                  <Draw
                    placeholder={
                      <div className={classes.loading}>
                        <PkSpinner />
                      </div>
                    }
                  />
                </DrawProvider>
              </DrawOptionsProvider>
            </div>
          ) : (
            <div className='pk-h--100' />
          )}
          <div className='pk-mt--m pk-w--100'>
            <PkButton appearance='outlined' linkHref={getDrawUrl(tab.round?.id, tab.draw.competitionId)}>
              {t(drawLive ? 'follow_live_draw' : 'go_to_draw_centre')}
            </PkButton>
          </div>
        </div>
        {!showDrawResults && !!competition?.code && (
          <Image
            className={clsx(classes.image, 'pk-w--100 pk-rounded--m')}
            src={`${imgBaseUrl}/imgml/uefacom/${competition.code}/draw/thumbnail.jpg`}
            fallback={fallbackImg}
          />
        )}
      </PkBox>
    </div>
  );
};

export default memo(DrawTab);
